import { useState, useCallback } from 'react';
import { TrackEvent } from '@koopajs/app';
import { IconButton, InputAdornment, SxProps, TextField, Theme } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { IUseSearchParamsResponse, useLocale } from '@koopajs/react';
import { useEffect } from 'react';
import Box from '@mui/material/Box';

const transformSearchInput = (input: string): string | undefined => {
  if (!input) return undefined;
  const searchInput = input
    .split(' ')
    .map((word) => `*${word}*`)
    .join(' ');
  return searchInput;
};

interface ISearchBarProps {
  isReloading: boolean;
  search: IUseSearchParamsResponse;
  sx?: SxProps<Theme>;
  placeholderText?: string;
}

export const SearchBar: React.FC<ISearchBarProps> = (props) => {
  const { search, isReloading, sx, placeholderText } = props;

  const [searchInput, setSearchInput] = useState('');

  const { t } = useLocale();
  const keyPrefix = 'Components.SearchBar';

  useEffect(() => {
    if (search.searchParams.query) {
      setSearchInput(search.searchParams.query.replace(/\*/g, ''));
    }
  }, []);

  const handleSearch = useCallback(
    (event: React.FormEvent<HTMLFormElement>): void => {
      event.preventDefault();
      search.setSearchParams({
        query: transformSearchInput(searchInput)
      });

      TrackEvent('searchBar');
    },
    [searchInput]
  );

  const handleResetSearch = useCallback((): void => {
    search.setSearchParams({
      query: undefined
    });
    setSearchInput('');
  }, []);

  const handleOnChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    setSearchInput(e.target.value);
  }, []);

  return (
    <Box
      component="form"
      onSubmit={handleSearch}
      sx={{
        border: 'none',
        ...sx
      }}
    >
      <TextField
        className="rr-block"
        placeholder={placeholderText || t(keyPrefix + '.placeholder')}
        sx={{
          width: '100%',
          '& .MuiOutlinedInput-root': {
            backgroundColor: '#FFFFFF',
            borderRadius: '100px',
            height: '40px',
            padding: '0',
            display: 'flex',
            alignItems: 'center',
            transition: 'box-shadow 0.3s ease',
            '& input': {
              padding: '8px 12px',
              fontSize: '14px'
            },
            '& input::placeholder': {
              color: '#4E5254',
              opacity: 1
            },
            '& fieldset': {
              border: 'none'
            },
            '&:hover fieldset': {
              border: 'none'
            },
            '&.Mui-focused fieldset': {
              border: 'none'
            }
          },
          '& .MuiOutlinedInput-root:focus-within': {
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
          },
          '& .MuiInputAdornment-root': {
            height: '100%',
            alignItems: 'center',
            margin: '0'
          },
          '& .MuiInputAdornment-positionStart': {
            marginLeft: '14px'
          },
          '& .MuiInputAdornment-positionEnd': {
            marginRight: '14px'
          },
          '& .MuiIconButton-root': {
            padding: '6px' // Adjust padding for the icon button
          }
        }}
        disabled={isReloading}
        value={searchInput}
        onChange={handleOnChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <IconButton edge="start">
                <SearchIcon />
              </IconButton>
            </InputAdornment>
          ),
          endAdornment: (
            <>
              {searchInput.length > 0 && (
                <InputAdornment position="end">
                  <IconButton onClick={handleResetSearch} edge="end">
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              )}
            </>
          )
        }}
      />
    </Box>
  );
};
